import Badge from '@atlaskit/badge';
import NotificationIcon from '@atlaskit/icon/glyph/notification';
import { colors } from '@atlaskit/theme';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { defineMessages, InjectedIntlProps, injectIntl } from 'react-intl';
import styled from 'styled-components';

import Store from '../../store';
import DrawerStore, { Drawer } from '../../store/drawerStore';
import NotificationsStore from '../../store/notificationsStore';
import FastTap from '../mobile/FastTap';

const Positioner = styled.div`
  position: relative;
`;

const BadgePositioner = styled.div`
  position: absolute;
  top: -5px;
  right: -7px;
`;

interface Props {
  drawerStore: DrawerStore;
  notificationsStore: NotificationsStore;
}

interface State {
  documentVisibilityState: VisibilityState;
}

@observer
class NotificationsDrawerButton extends React.Component<Props & InjectedIntlProps, State> {
  state = {
    documentVisibilityState: document.visibilityState,
  };

  private interval?: number;

  componentDidMount() {
    this.props.notificationsStore.countUnseenNotifications();
    this.interval = window.setInterval(this.maybeCountUnseenNotifications, 60000);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  componentWillUnmount() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = undefined;
    }
  }

  render() {
    const { intl } = this.props;
    const { unseenCount } = this.props.notificationsStore;
    return (
      <FastTap onClick={this.handleClick}>
        <Positioner>
          {unseenCount > 0 && (
            <BadgePositioner>
              <Badge max={9} appearance="important" value={unseenCount} />
            </BadgePositioner>
          )}
          <NotificationIcon label={intl.formatMessage(messages.notifications)} primaryColor={colors.N700} />
        </Positioner>
      </FastTap>
    );
  }

  private handleClick = () => {
    this.props.drawerStore.openDrawer(Drawer.Notifications);
    this.props.notificationsStore.acknowledgeUnseenNotificationsCount();
  };

  private maybeCountUnseenNotifications = () => {
    if (this.state.documentVisibilityState === 'visible') {
      this.props.notificationsStore.countUnseenNotifications();
    }
  };

  private onVisibilityChange = () => {
    // Immediately refresh the count when the tab becomes visible
    if (document.visibilityState === 'visible') {
      this.props.notificationsStore.countUnseenNotifications();
    }
    this.setState({
      documentVisibilityState: document.visibilityState,
    });
  };
}

export default inject(({ notificationsStore, drawerStore }: Store) => ({
  drawerStore,
  notificationsStore,
}))(injectIntl(NotificationsDrawerButton));

const messages = defineMessages({
  notifications: {
    defaultMessage: 'Notifications',
    description: `Button to show the user their notifications`,
    id: 'uchi.notificationsbutton.label',
  },
});
